import React, { useCallback, useEffect, useState } from 'react';
import { noop } from 'lodash';
import { stats } from '@fiverr-private/obs';
import { ErrorBoundary, LazyComponent } from '@fiverr-private/ui_utils';
import { OPEN_DRAWER_EVENT_NAME, WINDOW_INDICATION } from '@fiverr-private/dm_brief_creation_triggers';
import type { Rollouts } from '../../context/BriefCreationContext';
import { METRICS_PREFIX } from '../../constants';
import { Currency } from '../../types/currency';
import { logger } from '../../utils/logger';
import { defaultCurrency } from '../../context/BriefCreationContext.constants';
import BriefCreationDrawerWrapper, { BriefCreationDrawerWrapperProps } from './BriefCreationDrawerWrapper';

export interface DrawerProps {
  id?: string;
  source: string;
  currency: Currency;
  isOpen: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  initialPrompt?: string;
  onOpen?: () => void;
}

const setupEventHandler = (eventName, handler) => addEventListener(eventName, handler);

const removeEventHandler = (eventName, handler) => removeEventListener(eventName, handler);

export const markDrawerMounted = (isLoaded: boolean) => {
  window[WINDOW_INDICATION as string] = isLoaded;
};

export interface BriefCreationDrawerProps extends Rollouts {
  id?: string;
  enableEnhancedUx?: boolean;
  enableScreeningQuestions?: boolean;
}

export const BriefCreationDrawer = ({
  id,
  rollouts,
  enableEnhancedUx,
  enableScreeningQuestions,
}: BriefCreationDrawerProps) => {
  const [drawerProps, setDrawerProps] = useState<DrawerProps>({
    id,
    isOpen: false,
    currency: defaultCurrency,
    source: '',
    initialPrompt: '',
    onOpen: noop,
  });

  const setIsDrawerOpen = useCallback(
    (isOpen: boolean) => {
      if (drawerProps.isOpen !== isOpen) {
        setDrawerProps((prevProps) => ({ ...prevProps, isOpen }));
      }
    },
    [drawerProps]
  );

  useEffect(() => {
    const handleDrawerOpenEvent = (event: CustomEvent<DrawerProps>) => {
      if (id && event.detail.id !== id) return;

      setDrawerProps((previousProps) => ({
        ...previousProps,
        ...event.detail,
        isOpen: true,
      }));
    };

    setupEventHandler(OPEN_DRAWER_EVENT_NAME, handleDrawerOpenEvent);
    markDrawerMounted(true);

    return () => {
      removeEventHandler(OPEN_DRAWER_EVENT_NAME, handleDrawerOpenEvent);
      markDrawerMounted(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const briefCreationDrawerLazyImport = () =>
    import(/* webpackChunkName: "briefCreationDrawerWrapper" */ './BriefCreationDrawerWrapper');

  const onError = (error) => {
    logger.error(error, {
      description: 'Failed to lazy load BriefCreationDrawer',
    });
    stats.count(`${METRICS_PREFIX}.lazy_load_brief_creation_drawer`, 'error');
  };

  return (
    <ErrorBoundary onError={onError}>
      <LazyComponent<BriefCreationDrawerWrapperProps>
        lazyImport={briefCreationDrawerLazyImport}
        shouldImport={drawerProps.isOpen}
        onError={onError}
        componentProps={{
          rollouts,
          isOpen: drawerProps.isOpen,
          currency: drawerProps.currency,
          setIsOpen: setIsDrawerOpen,
          source: drawerProps.source,
          initialPrompt: drawerProps.initialPrompt,
          onOpen: drawerProps.onOpen,
          enableEnhancedUx,
          enableScreeningQuestions,
        }}
        beforeLoad={() => drawerProps.setIsLoading?.(true)}
        afterLoad={() => drawerProps.setIsLoading?.(false)}
      />
    </ErrorBoundary>
  );
};

export { BriefCreationDrawerWrapper };
